import React, { useState, useEffect, useMemo } from 'react';
import { usePriceContext } from '../context/PriceContext';
import ModalPrice from '../components/ModalPrice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PriceImporter from '../components/PriceImporter';
import { faPlus, faCheck, faXmark, faPen, faTag, faFileExcel, } from '@fortawesome/free-solid-svg-icons';

const PricePage = () => {
  const { price, fetchPrice, updatePrice, deletePrice, loading } = usePriceContext();
  const [activeType, setActiveType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => { if (!price.length) fetchPrice(); }, []);

  // Все уникальные типы (вкладки)
  const types = useMemo(() => {
    const set = new Set();
    price.forEach(p => { if (p.type) set.add(p.type); });
    return Array.from(set);
  }, [price]);

  useEffect(() => {
    if (!activeType && types.length) setActiveType(types[0]);
  }, [types, activeType]);

  const rows = useMemo(
    () => price.filter(p => p.type === activeType),
    [price, activeType]
  );

  // --- Импорт xlsx ---
  const [isImportOpen, setIsImportOpen] = useState(false);
  const { importPriceBatch } = usePriceContext();

  // --- Инлайн-редактирование ---
  const startEdit = (item) => {
    setEditingId(item._id);
    setDraft({
      partNumber: item.partNumber || '',
      brand: item.brand || '',
      analog: item.analog || '',
      name: item.name || '',
      stock: item.stock || '',
      priceRub: item.priceRub || '',
      priceEur: item.priceEur || '',
      weight: item.weight || '',
      supply: item.supply || '',
      comment: item.comment || '',
    });
  };

  const cancelEdit = () => { setEditingId(null); setDraft({}); };

  const saveEdit = async (id) => {
    try {
      await updatePrice(id, draft);
      setEditingId(null);
      setDraft({});
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить позицию?')) return;
    try { await deletePrice(id); } catch (e) { alert(e.message); }
  };

  const openPhoto = (photo) => {
    if (photo) setPhotoPreview(photo);
  };

  return (
    <div className="crm-page">
      <div className="crm-breadcrumb">
        <span className="crm-breadcrumb__item">Рабочий стол</span>
        <span className="crm-breadcrumb__sep">›</span>
        <span className="crm-breadcrumb__item crm-breadcrumb__item--active">Прайс</span>
      </div>

      <div className="crm-page-header">
        <h1 className="crm-page-title">
          <FontAwesomeIcon icon={faTag} style={{ marginRight: 10 }} />
          Прайс
        </h1>
        <div className="price-header-actions">
          <span className="price-count">Всего позиций: {price.length}</span>
          <button className="price-add-btn price-add-btn--ghost" onClick={() => setIsImportOpen(true)}>
            <FontAwesomeIcon icon={faFileExcel} />
            <span>Импорт из Excel</span>
          </button>
          <button className="price-add-btn" onClick={() => setIsModalOpen(true)}>
            <FontAwesomeIcon icon={faPlus} />
            <span>Добавить</span>
          </button>
        </div>
      </div>

      {/* Вкладки типов */}
      <div className="price-tabs">
        {types.length === 0 && !loading && (
          <div className="price-tabs__empty">Номенклатуры пока нет — нажмите «Добавить»</div>
        )}
        {types.map(t => (
          <button
            key={t}
            className={`price-tab ${t === activeType ? 'price-tab--active' : ''}`}
            onClick={() => { setActiveType(t); cancelEdit(); }}
          >
            {t}
            <span className="price-tab__count">{price.filter(p => p.type === t).length}</span>
          </button>
        ))}
      </div>

      {/* Таблица */}
      <div className="price-table-wrap">
        <table className="price-table">
          <thead>
            <tr>
              <th>Артикул</th>
              <th>Бренд</th>
              <th>Оригинал/Аналог</th>
              <th>Наименование</th>
              <th>Остаток</th>
              <th>Цена, ₽ Склад</th>
              <th>Цена, € Заказ</th>
              <th>Вес, кг</th>
              <th>Поставка</th>
              <th>Комментарий</th>
              <th>Фото</th>
              <th style={{ width: 90 }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={12} className="price-empty">Загрузка...</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={12} className="price-empty">В этой категории пока нет позиций</td></tr>
            )}

            {rows.map(item => {
              const isEdit = editingId === item._id;
              const cell = (field) => isEdit ? (
                <input
                  className="price-inline-input"
                  value={draft[field]}
                  onChange={(e) => setDraft({ ...draft, [field]: e.target.value })}
                />
              ) : (item[field] ?? '—');

              return (
                <tr key={item._id} className={isEdit ? 'price-row--edit' : ''}>
                  <td>{cell('partNumber')}</td>
                  <td>{cell('brand')}</td>
                  <td className="price-cell--wrap">{cell('analog')}</td>
                  <td className="price-cell--wrap">{cell('name')}</td>
                  <td>{cell('stock')}</td>
                  <td>{cell('priceRub')}</td>
                  <td>{cell('priceEur')}</td>
                  <td>{cell('weight')}</td>
                  <td>{cell('supply')}</td>
                  <td className="price-cell--wrap">{cell('comment')}</td>
                  <td>
                    {item.photo ? (
                      <img
                        src={item.photo}
                        alt=""
                        className="price-thumb"
                        onClick={() => openPhoto(item.photo)}
                      />
                    ) : <span className="price-thumb-empty">—</span>}
                  </td>
                  <td className="price-actions">
                    {isEdit ? (
                      <>
                        <button className="price-action price-action--save" onClick={() => saveEdit(item._id)} title="Сохранить">
                          <FontAwesomeIcon icon={faCheck} />
                        </button>
                        <button className="price-action price-action--cancel" onClick={cancelEdit} title="Отмена">
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="price-action price-action--edit" onClick={() => startEdit(item)} title="Редактировать">
                          <FontAwesomeIcon icon={faPen} />
                        </button>
                        <button className="price-action price-action--delete" onClick={() => handleDelete(item._id)} title="Удалить">
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isModalOpen && <ModalPrice onClose={() => setIsModalOpen(false)} types={types} />}
        {isImportOpen && (
        <PriceImporter
            onClose={() => setIsImportOpen(false)}
            onImport={async (items) => {
            const res = await importPriceBatch(items);
            // после импорта — перезагружаем прайс, чтобы вкладки типов появились
            await fetchPrice();
            // переключимся на первую новую вкладку, если она была создана
            const newType = items[0]?.type;
            if (newType) setActiveType(newType);
            return res;
            }}
            existingCount={price.length}
        />
        )}
      {photoPreview && (
        <div className="price-photo-overlay" onClick={() => setPhotoPreview(null)}>
          <img src={photoPreview} alt="" className="price-photo-big" onClick={(e) => e.stopPropagation()} />
          <button className="price-photo-close" onClick={() => setPhotoPreview(null)}>×</button>
        </div>
      )}
    </div>
  );
};

export default PricePage;